import { logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { analytics } from '../firebase/config';

// Firebase Analytics tracking service
export class FirebaseAnalyticsService {
  // Identify the signed-in user for GA4 reporting
  static setUser(userId: string) {
    if (!analytics) return;
    setUserId(analytics, userId);
  }

  static clearUser() {
    if (!analytics) return;
    setUserId(analytics, null);
  }

  static setUserContext(properties: {
    plan_tier?: string | null;
    org_role?: string | null;
  }) {
    if (!analytics) return;

    const payload: Record<string, string> = {};
    if (properties.plan_tier) {
      payload.plan_tier = properties.plan_tier;
    }
    if (properties.org_role) {
      payload.org_role = properties.org_role;
    }
    if (Object.keys(payload).length === 0) return;

    setUserProperties(analytics, payload);
  }

  // Track navigation events
  static trackNavigation(tabName: string, path: string) {
    if (!analytics) return;

    logEvent(analytics, 'page_view', {
      page_title: tabName,
      page_location: path,
      page_path: path,
    });

    // Also track as a custom navigation event
    logEvent(analytics, 'navigation_click', {
      tab_name: tabName,
      destination_path: path,
    });
  }

  // Track button clicks
  static trackButtonClick(
    buttonName: string,
    location: string,
    additionalData?: Record<string, any>,
  ) {
    if (!analytics) return;

    logEvent(analytics, 'button_click', {
      button_name: buttonName,
      location: location,
      ...additionalData,
    });
  }

  // Track feature usage
  static trackFeatureUsage(
    featureName: string,
    action: string,
    additionalData?: Record<string, any>,
  ) {
    if (!analytics) return;

    logEvent(analytics, 'feature_usage', {
      feature_name: featureName,
      action: action,
      ...additionalData,
    });
  }

  // Track user engagement
  static trackEngagement(
    engagementType: string,
    value?: number,
    additionalData?: Record<string, any>,
  ) {
    if (!analytics) return;

    logEvent(analytics, 'user_engagement', {
      engagement_type: engagementType,
      value: value,
      ...additionalData,
    });
  }

  // Track onboarding steps
  static trackOnboardingStep(
    stepName: string,
    stepNumber: number,
    completed: boolean,
  ) {
    if (!analytics) return;

    logEvent(analytics, 'onboarding_step', {
      step_name: stepName,
      step_number: stepNumber,
      completed: completed,
    });
  }

  // Track ecosystem metrics interactions
  static trackEcosystemMetrics(
    metricType: string,
    action: string,
    value?: number,
  ) {
    if (!analytics) return;

    logEvent(analytics, 'ecosystem_metrics', {
      metric_type: metricType,
      action: action,
      value: value,
    });
  }

  // Track playbook interactions
  static trackPlaybookInteraction(
    playbookType: string,
    action: string,
    additionalData?: Record<string, any>,
  ) {
    if (!analytics) return;

    logEvent(analytics, 'playbook_interaction', {
      playbook_type: playbookType,
      action: action,
      ...additionalData,
    });
  }
}

export default FirebaseAnalyticsService;
